/**
 * Badge — pill label, or a count bubble anchored to a child.
 *
 * Ported 1:1 from `tw-components.jsx` (grep `function Badge`). Deviations are
 * mechanical only: `cx` is imported, and there's no `import React`.
 *
 * Synced Aug 2026 to upstream's outlined-chip recipe: white surface, hairline
 * border in the hue, hue-700 label, and a hue-500 dot via `--dnd-badge-dot`
 * (the earlier tinted-fill palette — `bg-orange-50` etc. — is retired; upstream's
 * comment: "the brand pill in particular must never paint orange-50"). The count
 * bubble gained upstream's `dnd-badge-slide` entrance (keyframe in ./keyframes).
 *
 * No soft-fill variant on purpose. An app-only `tinted` palette (and a
 * `borderColor` override) was added Oct 2026 for the returns list's RMA/order
 * id chips and removed the same month once those became `IdPill`
 * (`OrdersTrackingList.jsx`) — a tinted chip read as disabled beside the order
 * pill, and its `brand` entry painted the forbidden orange-50. A clickable id
 * is an `IdPill`; a status is an outlined Badge. Don't bring the palette back.
 *
 * Two modes in one component. Passing both `count` and `children` gives the anchored
 * bubble (cart-icon style); otherwise it's a standalone pill coloured by `color`.
 */
import { cx } from "./cx";
import "./keyframes";

// Status badges are outlined chips: white surface, hairline in the hue, hue-700 label, hue-500 dot.
// (No tinted washes — the brand pill in particular must never paint orange-50.)
const BADGE_COLOR = {
  neutral:
    "bg-card border-neutral-200 text-neutral-700 [--dnd-badge-dot:var(--neutral-400)]",
  brand:
    "bg-card border-orange-200 text-orange-700 [--dnd-badge-dot:var(--orange-500)]",
  ocean:
    "bg-card border-blue-200 text-blue-700 [--dnd-badge-dot:var(--blue-500)]",
  info: "bg-card border-blue-200 text-blue-700 [--dnd-badge-dot:var(--blue-500)]",
  success:
    "bg-card border-success-100 text-success-700 [--dnd-badge-dot:var(--success-500)]",
  warning:
    "bg-card border-warning-100 text-warning-700 [--dnd-badge-dot:var(--warning-500)]",
  error:
    "bg-card border-error-100 text-error-700 [--dnd-badge-dot:var(--error-500)]",
  solid: "bg-orange-500 border-orange-500 text-white [--dnd-badge-dot:#fff]",
};

export function Badge({
  children,
  color = "neutral",
  size = "md",
  dot = false,
  count,
  max = 99,
  style = {},
  className = "",
  ...rest
}) {
  if (count !== undefined && children) {
    const display = count > max ? `${max}+` : count;
    return (
      <span
        className="dnd-badge-anchor relative inline-flex"
        style={style}
        {...rest}
      >
        {children}
        {count > 0 && (
          <span
            key={display}
            style={{
              animation:
                "dnd-badge-slide 260ms cubic-bezier(0.22, 1, 0.36, 1), dnd-badge-pop 500ms cubic-bezier(0.34, 1.36, 0.64, 1)",
            }}
            className="dnd-badge-count absolute top-[-6px] right-[-6px] min-w-[18px] h-[18px] px-[5px] inline-flex items-center justify-center bg-orange-500 text-white font-sans font-bold text-[11px] leading-none rounded-[var(--radius-pill)] border-2 border-card animate-[dnd-badge-pop_.45s_var(--ease-spring)]"
          >
            {display}
          </span>
        )}
      </span>
    );
  }
  return (
    <span
      className={cx(
        "dnd-badge inline-flex items-center font-sans font-semibold leading-none tracking-[0.01em] rounded-[var(--radius-pill)] whitespace-nowrap border",
        size === "sm"
          ? "gap-[5px] py-[3px] px-[9px] text-2xs"
          : "gap-[6px] py-[5px] px-[11px] text-xs",
        BADGE_COLOR[color] || BADGE_COLOR.neutral,
        className
      )}
      style={style}
      {...rest}
    >
      {dot && (
        <span className="dnd-badge__dot w-[6px] h-[6px] rounded-full bg-[var(--dnd-badge-dot,currentColor)]" />
      )}
      {children}
    </span>
  );
}
